function buildAwsDates() {
  const dateISOString = new Date(Date.now() + 864e5).toISOString();

  return {
    dateISOString,
    xAmzDate: dateISOString.split("-").join("").split(":").join("").split(".").join(""),
    dateYMD: dateISOString.split("T")[0].split("-").join(""),
  };
}

function throwConfigError(config, file) {
  if (config.bucketName === null || config.bucketName === "") {
    throw new Error("Your bucketName cannot be empty");
  }

  if (config.region === null || config.region === "") {
    throw new Error("Must provide a valid region in order to use your bucket");
  }

  if (config.accessKeyId === null || config.accessKeyId === "") {
    throw new Error("Must provide accessKeyId");
  }

  if (config.secretAccessKey === null || config.secretAccessKey === "") {
    throw new Error("Must provide secretAccessKey");
  }

  if (!file) {
    throw new Error("File cannot be empty");
  }
}

function getUploadUrl(config) {
  if (config.s3Url && config.s3Url !== "") {
    return config.s3Url;
  }

  const countryCode = config.region.split("-")[0];

  if (countryCode === "cn") {
    return `https://${config.bucketName}.s3.${config.region}.amazonaws.com.${countryCode}`;
  }

  return `https://${config.bucketName}.s3-${config.region}.amazonaws.com`;
}

function createPolicy(config, dates, acl) {
  const policy = {
    expiration: dates.dateISOString,
    conditions: [
      { acl },
      { bucket: config.bucketName },
      ["starts-with", "$key", `${config.dirName ? `${config.dirName}/` : ""}`],
      ["starts-with", "$Content-Type", ""],
      ["starts-with", "$x-amz-meta-tag", ""],
      { "x-amz-algorithm": "AWS4-HMAC-SHA256" },
      {
        "x-amz-credential": `${config.accessKeyId}/${dates.dateYMD}/${config.region}/s3/aws4_request`,
      },
      { "x-amz-date": dates.xAmzDate },
      { "x-amz-meta-uuid": "14365123651274" },
      { "x-amz-server-side-encryption": "AES256" },
    ],
  };

  return btoa(JSON.stringify(policy)).replace(/\n|\r/g, "");
}

function getSubtleCrypto() {
  const subtle = globalThis.crypto && globalThis.crypto.subtle;

  if (!subtle) {
    throw new Error("Web Crypto API is unavailable in this environment");
  }

  return subtle;
}

function normalizeKeyBytes(key) {
  if (typeof key === "string") {
    return new TextEncoder().encode(key);
  }

  if (key instanceof Uint8Array) {
    return key;
  }

  if (key instanceof ArrayBuffer) {
    return new Uint8Array(key);
  }

  return new Uint8Array(key.buffer, key.byteOffset, key.byteLength);
}

async function signHmac(key, message) {
  const subtle = getSubtleCrypto();
  const encodedKey = normalizeKeyBytes(key);
  const cryptoKey = await subtle.importKey(
    "raw",
    encodedKey,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  const signature = await subtle.sign("HMAC", cryptoKey, new TextEncoder().encode(message));

  return new Uint8Array(signature);
}

function bytesToHex(bytes) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function createSignature(config, dateYMD, policyBase64) {
  // SigV4 derives a signing key by chaining HMACs over date, region, and service.
  const kDate = await signHmac(`AWS4${config.secretAccessKey}`, dateYMD);
  const kRegion = await signHmac(kDate, config.region);
  const kService = await signHmac(kRegion, "s3");
  const kSigning = await signHmac(kService, "aws4_request");
  const signature = await signHmac(kSigning, policyBase64);

  return bytesToHex(signature);
}

export default class AWSS3UploadAshClient {
  constructor(config = {}) {
    this.config = config;
  }

  async uploadFile(file, contentType, presignedURL, newFileName, acl) {
    if (!file) {
      throw new Error("File cannot be empty");
    }

    if (!presignedURL) {
      throwConfigError(this.config, file);
    }

    if (presignedURL) {
      try {
        const response = await fetch(presignedURL, {
          method: "PUT",
          headers: {
            "Content-Type": contentType,
          },
          body: file,
        });

        return {
          status: response.status,
          body: response.ok ? "Upload complete" : "Upload failed",
        };
      } catch (error) {
        return {
          status: 400,
          body: "Upload failed",
        };
      }
    }

    const fileName = newFileName || file.name;
    const key = `${this.config.dirName ? `${this.config.dirName}/` : ""}${fileName}`;
    const url = getUploadUrl(this.config);
    const aclValue = acl === undefined || acl === "" ? "public-read" : acl;
    const dates = buildAwsDates();
    const policy = createPolicy(this.config, dates, aclValue);
    const signature = await createSignature(this.config, dates.dateYMD, policy);
    const formData = new FormData();

    formData.append("key", key);
    formData.append("acl", aclValue);
    formData.append("Content-Type", contentType);
    formData.append("x-amz-meta-uuid", "14365123651274");
    formData.append("x-amz-server-side-encryption", "AES256");
    formData.append("X-Amz-Date", dates.xAmzDate);
    formData.append(
      "X-Amz-Credential",
      `${this.config.accessKeyId}/${dates.dateYMD}/${this.config.region}/s3/aws4_request`
    );
    formData.append("X-Amz-Algorithm", "AWS4-HMAC-SHA256");
    formData.append("x-amz-meta-tag", "");
    formData.append("Policy", policy);
    formData.append("X-Amz-Signature", signature);
    formData.append("file", file);

    const response = await fetch(url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      return Promise.reject(response);
    }

    return {
      bucket: this.config.bucketName,
      key,
      location: `${url}${this.config.dirName ? `${this.config.dirName}/` : ""}${fileName}`,
      status: response.status,
    };
  }

  async deleteFile(fileName) {
    const url = `https://${this.config.bucketName}.s3${
      this.config.region ? `-${this.config.region}` : ""
    }.amazonaws.com/${this.config.dirName ? `${this.config.dirName}/` : ""}${fileName}`;

    const response = await fetch(url, { method: "DELETE" });

    if (!response.ok) {
      return Promise.reject(response);
    }

    return {
      ok: response.ok,
      status: response.status,
      message: "File Deleted",
      fileName: this.config.dirName ? `${this.config.dirName}/${fileName}` : fileName,
    };
  }
}
